import type { GameState } from '../state';
import {
  getDistrict,
  type DistrictId,
  POP_BASE,
  POP_PER_WAVE,
  DISTRICT_CAP_BASE,
  CAP_PER_HOUSING,
  BONUS_PER_WORKER,
  DISTRICT_BONUS_CAP,
  TOTAL_BONUS_CAP,
} from '../data/districts';

/**
 * Districts sim (data/districts.ts holds the numbers). Population is DERIVED
 * from wave progress past the unlock frontier — nothing to step, nothing to
 * desync between online and offline.
 */

export interface DistrictsOpResult {
  ok: boolean;
  reason?: string;
}

/** Citizens aboard: starters + rescues per wave pushed since unlock + grants. */
export function population(state: GameState): number {
  const d = state.districts;
  if (!d.unlocked) return 0;
  const rescued = Math.max(0, state.combat.highestWave - d.baselineWave) * POP_PER_WAVE;
  return POP_BASE + rescued + d.popBonus;
}

export function assignedTotal(state: GameState): number {
  const a = state.districts.assigned;
  return a.housing + a.factory + a.lab + a.arcade;
}

export function unassigned(state: GameState): number {
  return Math.max(0, population(state) - assignedTotal(state));
}

/** Working-district capacity (Housing raises it). Housing itself is uncapped. */
export function districtCap(state: GameState, id: DistrictId): number {
  if (id === 'housing') return Number.MAX_SAFE_INTEGER;
  return DISTRICT_CAP_BASE + CAP_PER_HOUSING * state.districts.assigned.housing;
}

export function assignPop(state: GameState, id: DistrictId, n: number): DistrictsOpResult {
  const d = state.districts;
  if (!d.unlocked) return { ok: false, reason: 'No city to staff.' };
  if (!getDistrict(id)) return { ok: false, reason: 'Unknown district.' };
  const free = unassigned(state);
  if (free <= 0) return { ok: false, reason: 'Everyone already has a job. Rescue more people (push waves).' };
  const room = districtCap(state, id) - d.assigned[id];
  if (room <= 0) return { ok: false, reason: 'District is full. Housing raises capacity.' };
  d.assigned[id] += Math.max(1, Math.min(Math.floor(n), free, room));
  return { ok: true };
}

export function unassignPop(state: GameState, id: DistrictId, n: number): DistrictsOpResult {
  const d = state.districts;
  if (!getDistrict(id)) return { ok: false, reason: 'Unknown district.' };
  if (d.assigned[id] <= 0) return { ok: false, reason: 'Nobody works there.' };
  const take = Math.max(1, Math.min(Math.floor(n), d.assigned[id]));
  d.assigned[id] -= take;
  // Pulling Housing can strand workers over the new caps: lay off the excess.
  if (id === 'housing') {
    for (const w of ['factory', 'lab', 'arcade'] as const) {
      const cap = districtCap(state, w);
      if (d.assigned[w] > cap) d.assigned[w] = cap;
    }
  }
  return { ok: true };
}

export interface DistrictBonuses {
  /** Fractions (0.05 = +5%). */
  scrap: number;
  components: number;
  petDrop: number;
  total: number;
}

/** Per-district worker bonuses, each capped, the sum capped harder. */
export function districtBonuses(state: GameState): DistrictBonuses {
  if (!state.districts.unlocked) return { scrap: 0, components: 0, petDrop: 0, total: 0 };
  const a = state.districts.assigned;
  let scrap = Math.min(DISTRICT_BONUS_CAP, BONUS_PER_WORKER * a.factory);
  let components = Math.min(DISTRICT_BONUS_CAP, BONUS_PER_WORKER * a.lab);
  let petDrop = Math.min(DISTRICT_BONUS_CAP, BONUS_PER_WORKER * a.arcade);
  const sum = scrap + components + petDrop;
  if (sum > TOTAL_BONUS_CAP) {
    const k = TOTAL_BONUS_CAP / sum;
    scrap *= k;
    components *= k;
    petDrop *= k;
  }
  return { scrap, components, petDrop, total: Math.min(TOTAL_BONUS_CAP, sum) };
}

/** Power up the city — rides the MechCorp City kill. Population accrues from
 *  the frontier at unlock, so veterans aren't owed back-pay. */
export function unlockDistricts(state: GameState): void {
  if (state.districts.unlocked) return;
  state.districts.unlocked = true;
  state.districts.baselineWave = state.combat.highestWave;
  state.meta.unlocks.districts = true;
}
